import {
  Select,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { Scalars } from '../../../../__generated-gql-types__/globalTypes';
import { isCoachAvailabilityOnboarded } from '../../../../utils/availability';
import { trackFirstMessageSent } from '../../../../utils/message';
import { wasMutationSuccessful } from '../../../../utils/mutations';
import { Textarea } from '../../../forms/Inputs';
import CheckboxInput from '../../../inputs/CheckboxInput';

import {
  type IntroMessageFormCoachFragment,
  type IntroMessageFormRequestIntroMessageMutationVariables,
  useIntroMessageFormRequestIntroMessageMutation,
} from './__generated-gql-types__/IntroMessageForm.generated';

const REGISTER_OPTIONS = Object.freeze({
  MESSAGE: {
    required: {
      value: true,
      message: 'Message is required',
    },
  },
});

interface IntroMessageFormProps {
  coach: IntroMessageFormCoachFragment;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  defaultMessage: string;
  onSuccess?: () => void;
  onScheduleIntroCall?: () => void;
  onRequestClose: () => void;
}

export const IntroMessageForm: FC<IntroMessageFormProps> = ({
  coach,
  categoryUrn,
  defaultMessage,
  onSuccess,
  onRequestClose,
}) => {
  const { showToast } = useToast();

  const isAvailabilityRamped = true;

  const coachCategoriesOptions = coach.categoryInformationList?.map(
    (category) => ({
      value: category.category.id,
      label: category.category.name,
    }),
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IntroMessageFormRequestIntroMessageMutationVariables>({
    defaultValues: {
      categoryUrn: categoryUrn ?? coachCategoriesOptions?.[0]?.value,
      introCallRequested: isAvailabilityRamped
        ? !isCoachAvailabilityOnboarded(coach.availability?.defaultAvailability)
        : true,
      message: defaultMessage,
    },
  });

  useEffect(() => {
    register('introCallRequested');
    register('categoryUrn');
  }, [register]);

  const [requestIntroMessage] =
    useIntroMessageFormRequestIntroMessageMutation();

  const watchIntroCallRequested = watch('introCallRequested');

  const onSubmitWrapper: SubmitHandler<
    IntroMessageFormRequestIntroMessageMutationVariables
  > = async (values) => {
    const res = await requestIntroMessage({
      variables: {
        ...values,
        coachUrn: coach.id,
      },
    });

    if (!wasMutationSuccessful(res)) {
      showToast({
        type: ToastType.ERROR,
        duration: ToastDuration.NORMAL,
        message: "Couldn't send your message. Please try again.",
      });
      console.warn(res.errors);
      throw new Error('Failed to send message');
    } else {
      trackFirstMessageSent({
        coachUrn: coach.id,
      });
      onSuccess?.();
      onRequestClose();
      showToast({
        type: ToastType.SUCCESS,
        duration: ToastDuration.NORMAL,
        message: 'Message sent.',
      });
    }
  };

  return (
    <form
      id="intro-message-form"
      className="grid gap-6"
      onSubmit={handleSubmit(onSubmitWrapper)}
    >
      <h3 className="text-lg leading-6 text-leland-gray-extra-light">
        Kick-off your coaching relationship by starting a conversation with{' '}
        {coach.user.firstName}.
      </h3>
      {categoryUrn ? null : (
        <Select
          label="Select a category of interest"
          options={coachCategoriesOptions ?? []}
          onValueChange={(value) => setValue('categoryUrn', value)}
          value={watch('categoryUrn')}
        />
      )}
      <Textarea
        label="Customize your message below"
        labelClass="text-base"
        placeholder={defaultMessage}
        register={register('message', REGISTER_OPTIONS.MESSAGE)}
        rows={4}
        error={errors.message}
      />
      {isAvailabilityRamped ? null : (
        <div className="rounded-2xl bg-leland-gray-hover p-6">
          <CheckboxInput
            label={
              <div className="ml-1 flex flex-col items-start justify-center font-normal">
                <p className="text-base text-leland-gray-dark">
                  Request a free intro call with this message
                </p>
                <p className="mt-1.5 text-sm text-leland-gray-light">
                  Intro calls are a great way to meet a coach and determine
                  whether a longer-term coaching relationship would be a good
                  fit. Please note that Intro Calls are not meant for actual
                  coaching; please be respectful of the coach&rsquo;s time.
                </p>
              </div>
            }
            isChecked={!!watchIntroCallRequested}
            onToggle={() =>
              setValue('introCallRequested', !watchIntroCallRequested)
            }
          />
        </div>
      )}
    </form>
  );
};
